import { useEffect, useRef, useState, FC } from 'react';
import { useHistory } from 'react-router-dom';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';

//Components
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import ActivityLoading from '../common/ActivityLoading';
import ActivityModal from '../common/ActivityModal';
import ActivityToast from '../common/ActivityToast';

//Services
import firebaseService from '../../firebase/FirebaseService';
import { getLoggedUser, getUserById } from '../../services/UserService';
import { getSubscribedActivitiesByUserId, getJunctionByStudentAndActivity, removeActivityFromUser, registerUserToDatabase } from '../../services/NewGroupService';

//Types
import { ActivityInterface, ModalOptionsInterface, ToastOptionsInterface } from '../common/Types';
import SendNotifications from '../common/SendNotifications';

const moment = extendMoment(Moment);

interface ActivityListInterface {
    activities: Array<any>;
    cyclesIds: Array<any>;
    handleInscription: any;
    goToActivityDetails: any;
    authorized: any;
}

interface ActivityCardInterface {
    activity: any;
    cycleAvailable: boolean;
    handleInscription: any;
    goToActivityDetails: any;
}

const ActivityCard: FC<ActivityCardInterface> = (props: ActivityCardInterface) => {
    const { activity, cycleAvailable, handleInscription, goToActivityDetails } = props;

    return (
        <Card>
            <Card.Img style={{ width: '100%' }} variant="top" src={activity.image_url ? activity.image_url : "./cccr-default.jpg"} />
            <Card.Body>
                <Card.Title>{activity.name}</Card.Title>
                <Card.Subtitle>{`Registered: ${activity.inscribed}/${activity.students_limit}`}</Card.Subtitle>
                <Card.Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', minHeight: '100px', maxHeight: '100px', textAlign: 'justify', marginTop: '10px' }}>
                    {activity.description}
                </Card.Text>
                <div style={{ display: 'flex', flexDirection: 'row', marginTop: '10px', alignItems: 'center' }}>
                    <Button disabled={!cycleAvailable} variant="primary" onClick={() => handleInscription(activity)} style={{ width: '100%' }} >Unregister</Button>
                    <Button disabled={!cycleAvailable} variant="primary" onClick={() => goToActivityDetails(activity)} style={{ marginLeft: '10px', width: '100%' }}>Details</Button>
                </div>
            </Card.Body>
        </Card>
    )
}

const CycleSection: FC<any> = (props: any) => {
    const { activities, cycle, goToActivityDetails, handleInscription } = props;

    const now = moment();
    const startDate = moment(cycle?.start_date.toDate());
    const endDate = moment(cycle?.end_date.toDate());

    const range = moment.range(startDate, endDate);
    const cycleAvailable = range.contains(now);

    return (
        <Row key={cycle.id} xs={1} sm={2} md={3} className="g-4">
            <div style={{ width: '100%', fontSize: '20px', fontWeight: 'bolder' }}>
                {cycle.name}
            </div>
            {activities && activities.map((activity: any, idx: any) => (
                activity.cycle === cycle.id ?
                    <Col key={idx} style={{ marginBottom: '1.5rem' }}>
                        <ActivityCard
                            cycleAvailable={cycleAvailable}
                            activity={activity}
                            handleInscription={handleInscription}
                            goToActivityDetails={goToActivityDetails}
                        />
                    </Col> : null
            ))}
        </Row>
    )
}

const ActivityList: FC<ActivityListInterface> = (props: ActivityListInterface) => {
    const { activities, cyclesIds, handleInscription, goToActivityDetails, authorized } = props;

    cyclesIds?.sort((d1, d2) => d2.end_date.toDate().getTime() - d1.end_date.toDate().getTime());

    return (
        <div>
             {cyclesIds.length > 0 ?
                (cyclesIds?.map((cycle: any, idx: number) => (
                    <CycleSection key={idx} activities={activities} cycle={cycle} goToActivityDetails={goToActivityDetails} handleInscription={handleInscription} authorized={authorized} />
                ))) : (<SendNotifications message={(authorized?.admin ? "No te has registrado a ninguna actividad" : (authorized?.student ? "You have not registered any activity" : "No te has registrado a ninguna actividad"))} />)
            }
        </div>
    )
}

const MyActivities: FC = (props: any) => {
    const { location } = props;

    const [loading, setLoading] = useState(false);
    const [activities, setActivities] = useState<any>(undefined);
    const [cyclesIds, setCyclesIds] = useState<Array<any>>([]);
    const [user, setUser] = useState<any>(undefined);
    const [authorized, setAuthorized] = useState<any>(undefined);
    const [modal, setModal] = useState<ModalOptionsInterface>({ open: false, header: "", body: "", buttons: [] });
    const [toastOptions, setToastOptions] = useState<ToastOptionsInterface>({ open: false, message: "" });

    const history = useHistory();
    let ref = useRef<any>(null);

    useEffect(() => {
        async function initialLoad() {
            const state = location.state ? location.state : undefined;
            let userResp: any;

            setLoading(true); 
            activitiesListener();
            
            if (state !== undefined) {
                userResp = await getUserById(state.email);
            } else {
                userResp = await getLoggedUser();
            }

            if (userResp && userResp.email) {
                setUser(userResp);
                fetchUserActivities(userResp);
            } else {
                setActivities([]);
                setLoading(false);
            }
            registerUserToDatabase().then((response: any) => {
                setAuthorized(response);
            });
        }

        initialLoad();
    }, [location]);

    const activitiesListener = () => {
        firebaseService.db?.collection("activities")
            .onSnapshot((snapshot) => {
                snapshot.docChanges().forEach(async (change) => {
                    const { activities, user } = ref.current;
                    if (change.type === "modified") {
                        const modifiedActivity = activities?.find((act: any) => act.id === change.doc.id);

                        if (modifiedActivity) {
                            const activitiesFiltered = activities.filter((act: any) => act.id !== change.doc.id);

                            modifiedActivity.reserved = change.doc.data().reserved;
                            modifiedActivity.inscribed = change.doc.data().inscribed;

                            const junction = await getJunctionByStudentAndActivity(user.email, change.doc.id);
                            modifiedActivity.subscribed = junction !== undefined;

                            activitiesFiltered.push(modifiedActivity);
                            setActivities(activitiesFiltered)
                        }
                    }
                });
            });
    }

    useEffect(() => {
        const cyclesIds: Array<any> = [];
        activities?.forEach((element: any) => {
            if (!cyclesIds.find((cyc) => cyc.id === element?.cycle) && element?.cycleData)
                cyclesIds.push({ ...element?.cycleData });
        });

        setCyclesIds(cyclesIds);
    }, [activities]);

    useEffect(() => {
        ref.current = { activities, user };
    }, [activities, user]);

    const fetchUserActivities = (u: any) => {
        getSubscribedActivitiesByUserId(u.email).then((actResp) => {
            setUser(u);
            setActivities(actResp);
            setLoading(false);
        })
    }

    const goToActivityDetails = (activity: ActivityInterface) => {
        history.push({ pathname: `/activityDetails/${activity.id}` });
    }

    const handleInscription = (activity: any) => {
        setModal({
            open: true,
            header: "Unregister",
            body: "Are you sure you want to unregister from this activity?",
            buttons: [
                { text: "Unregister", func: () => saveInscription(activity) },
                { text: "Cancel", func: handleModalClose }
            ]
        });
    }

    const saveInscription = (activity: any) => {
        setLoading(true);
        if (activity && user?.email) {
            removeActivityFromUser(user.email, activity.id).then((response) => {
                fetchUserActivities(user);
                handleModalClose();

                if (response.code === "success") {
                    setToastOptions({ open: true, message: response.message });
                }
            })
        }
    }

    const handleModalClose = () => {
        setModal({ open: false, header: "", body: "", buttons: [] });
    }

    if (loading) return <ActivityLoading />

    return (
        <Container>
            <div style={{ marginBottom: '20px' }}>
                <h1>My activities</h1>
            </div>
            <ActivityModal {...modal} onClose={handleModalClose} />
            <ActivityToast {...toastOptions} onClose={() => setToastOptions({ open: false, message: "" })} autohide={4000} />
            <ActivityList
                activities={activities}
                cyclesIds={cyclesIds}
                handleInscription={handleInscription}
                goToActivityDetails={goToActivityDetails}
                authorized={authorized}
            />
        </Container>
    )
}

export default MyActivities;
